import type { FilterCriteria } from '@openx/components-filters/lib/FiltersBar/types.ts';
import type { ConstValues } from '@openx/types/helpers.ts';
import type { Option } from '@openx/types/options.ts';
import type { UseFetchSegmentsPayload } from '@openx/types/targeting/customVariables.ts';
import { useCallback, useMemo, useState } from 'react';
import { normalizeProviderId } from '../../utils/enrichment.ts';

export const CONTEXTUAL_SEGMENT_SOURCES = {
  All: 'all',
  My: 'my',
} as const;

export type ContextualSegmentSource = ConstValues<typeof CONTEXTUAL_SEGMENT_SOURCES>;

export type ContextualSegmentFilters = FilterCriteria & {
  dataPartner?: string[];
  price?: string[];
  pricePom?: string[];
  priceCpm?: string[];
};

export type ContextualSegmentFilterValues = {
  phrase: string;
  source: ContextualSegmentSource;
  filters: ContextualSegmentFilters;
};

const hasFilterValue = (filters: ContextualSegmentFilters) =>
  Object.values(filters).some(value =>
    Array.isArray(value) ? value.some(item => item?.trim()) : value !== undefined && value !== null && value !== ''
  );

export const mergeContextualDataPartnerOptions = (
  sellableOptions: Option[],
  currentProviderIds: string[]
): Option[] => {
  const mergedOptions = new Map<string, Option>();

  for (const option of sellableOptions) {
    const providerId = normalizeProviderId(option.id);
    if (providerId) mergedOptions.set(providerId, { ...option, id: providerId });
  }

  for (const currentProviderId of currentProviderIds) {
    const providerId = normalizeProviderId(currentProviderId);
    if (providerId && !mergedOptions.has(providerId)) {
      mergedOptions.set(providerId, { id: providerId, name: providerId });
    }
  }

  return Array.from(mergedOptions.values());
};

const toPriceRange = (range?: string[]) => {
  const [minimum, maximum] = range ?? [];
  const min = minimum ? Number(minimum) : undefined;
  const max = maximum ? Number(maximum) : undefined;

  return min == null && max == null ? undefined : { min, max };
};

export const buildContextualSegmentFetchFilters = (
  phrase: string,
  source: ContextualSegmentSource,
  filters: ContextualSegmentFilters,
  accountProviderIds: string[]
): Pick<UseFetchSegmentsPayload, 'phrase' | 'provider_id' | 'price_cpm' | 'price_pom'> => {
  const dataPartnerProviderIds = filters.dataPartner?.filter(Boolean) ?? [];
  const myProviderIds = accountProviderIds.map(normalizeProviderId).filter(Boolean);
  const priceCpm = toPriceRange(filters.priceCpm);
  const pricePom = toPriceRange(filters.pricePom);
  const providerIds = source === CONTEXTUAL_SEGMENT_SOURCES.My ? myProviderIds : dataPartnerProviderIds;

  return {
    ...(phrase.trim() ? { phrase: phrase.trim() } : {}),
    ...(providerIds?.length ? { provider_id: providerIds } : {}),
    ...(priceCpm ? { price_cpm: priceCpm } : {}),
    ...(pricePom ? { price_pom: pricePom } : {}),
  };
};

export const useContextualSegmentFilters = () => {
  const [phrase, setPhrase] = useState('');
  const [source, setSource] = useState<ContextualSegmentSource>(CONTEXTUAL_SEGMENT_SOURCES.All);
  const [filters, setFilters] = useState<ContextualSegmentFilters>({});

  const hasActiveFilters = useMemo(
    () => phrase.trim() !== '' || source !== CONTEXTUAL_SEGMENT_SOURCES.All || hasFilterValue(filters),
    [filters, phrase, source]
  );

  const clearAll = useCallback(() => {
    setPhrase('');
    setSource(CONTEXTUAL_SEGMENT_SOURCES.All);
    setFilters({});
  }, []);

  return { phrase, setPhrase, source, setSource, filters, setFilters, hasActiveFilters, clearAll };
};
